import { useMemo, useState } from "react";
import ReactTooltip from "react-tooltip";

import { LISSpecimen, SpecimenProtocolOverride } from "pwapi";

import { LabList } from "custom-models/frontLoader";

import {
  getProtocolStatuses,
  protocolLabels,
} from "shared/utils/specimenUtils";

import { updateSpecimen } from "app/actions/specimenActions";
import { useAppDispatch, useAppSelector } from "app/hooks";

import PopUpDialog from "./PopUpDialog";
import "./SpecimenProtocolOverrideField.css";

const SpecimenProtocolOverrideField = ({
  specimen,
  accessioningLab,
  caseType,
}: {
  specimen: LISSpecimen & { protocol_overrides?: SpecimenProtocolOverride[] };
  accessioningLab?: LabList;
  caseType: Parameters<typeof getProtocolStatuses>[3];
}) => {
  const dispatch = useAppDispatch();

  const difEnabled = useAppSelector(
    (state) => !!state.sys.features.switches?.difProtocol
  );
  const slowMohsEnabled = useAppSelector(
    (state) => !!state.sys.features.switches?.slowMohsProtocol
  );

  const [pendingOverride, setPendingOverride] = useState<{
    protocol: SpecimenProtocolOverride.protocol;
    value: string;
  } | null>(null);

  const protocolOverrides = specimen.protocol_overrides || [];
  const protocolStatuses = getProtocolStatuses(
    specimen,
    true,
    accessioningLab?.settings,
    caseType
  );

  const pashReflexEnabled = accessioningLab?.settings?.pash_reflex?.active;
  const pasfReflexEnabled = accessioningLab?.settings?.pasf_reflex?.active;

  const protocolsToShow: SpecimenProtocolOverride.protocol[] = useMemo(() => {
    const protocols: SpecimenProtocolOverride.protocol[] = [
      SpecimenProtocolOverride.protocol.ALOPECIA,
      SpecimenProtocolOverride.protocol.NAIL,
    ];

    if (difEnabled) protocols.push(SpecimenProtocolOverride.protocol.DIF);
    if (pashReflexEnabled)
      protocols.push(SpecimenProtocolOverride.protocol.PASH_REFLEX);
    if (pasfReflexEnabled)
      protocols.push(SpecimenProtocolOverride.protocol.PASF_REFLEX);
    if (slowMohsEnabled)
      protocols.push(SpecimenProtocolOverride.protocol.SLOW_MOHS);

    return protocols;
  }, [difEnabled, pashReflexEnabled, pasfReflexEnabled, slowMohsEnabled]);

  const requestProtocolOverride = (
    p: SpecimenProtocolOverride.protocol,
    v: string
  ) => {
    setPendingOverride({ protocol: p, value: v });
  };

  const applyProtocolOverride = () => {
    if (!pendingOverride) return;
    const { protocol: p, value: v } = pendingOverride;

    const newProtocolOverrides = protocolOverrides.filter(
      (po) => po.protocol !== p
    );

    if (v !== "null") {
      newProtocolOverrides.push({ protocol: p, active: v === "true" });
    }

    if (specimen.id) {
      dispatch(
        updateSpecimen({
          id: specimen.id,
          requestBody: {
            name: specimen.name || "",
            protocol_overrides: newProtocolOverrides,
          },
        })
      );
    }

    setPendingOverride(null);
  };

  const cancelProtocolOverride = () => {
    setPendingOverride(null);
  };

  return (
    <div className="Panel">
      <fieldset className="panelContainer__section">
        <legend className="panelContainer__section--title">Protocols</legend>
        {protocolsToShow.map((p) => {
          const override = protocolOverrides.find((po) => po.protocol === p);
          const status = protocolStatuses[p];
          const isActive =
            override !== undefined ? override.active : status.active;
          const specimenProtocolTagStyle = isActive
            ? "Tag--darkblue"
            : "Tag--gray";

          return (
            <div style={{ display: "flex" }} key={p}>
              <div
                className={`Tag ${specimenProtocolTagStyle}`}
                data-tip={status.reason}
                data-for="specimen-protocol-override-field"
              >
                {protocolLabels[p]}
              </div>
              <div style={{ marginLeft: "auto" }}>
                <select
                  onChange={(ev) =>
                    requestProtocolOverride(p, ev.currentTarget.value)
                  }
                  value={
                    override === undefined
                      ? "null"
                      : override.active
                      ? "true"
                      : "false"
                  }
                >
                  <option value="null">
                    Auto: {status.active ? "ON" : "OFF"}
                  </option>
                  <option value="true">Override: ON</option>
                  <option value="false">Override: OFF</option>
                </select>
              </div>
            </div>
          );
        })}
        <ReactTooltip id="specimen-protocol-override-field" place="right" />
      </fieldset>

      {pendingOverride && (
        <PopUpDialog
          title="Reset grossing values?"
          onClose={cancelProtocolOverride}
        >
          <>
            <p>
              Changing this protocol override will reset any values entered in
              the grossing form. This cannot be undone.
            </p>
            <div className="PopUpDialog__actions">
              <button onClick={cancelProtocolOverride}>Cancel</button>
              <button onClick={applyProtocolOverride}>Proceed</button>
            </div>
          </>
        </PopUpDialog>
      )}
    </div>
  );
};

export default SpecimenProtocolOverrideField;
